import { useState } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi, type NotificationRecord } from '@/api/client';
import { useAsync } from '@/hooks';
import { Button } from './Button';
import { useToast } from './Feedback';
import { NOTIFICATIONS_CHANGED } from './Nav';
import { formatDateTime, humanize } from '@/lib/format';

const TIER_LABELS: Record<string, string> = {
  act_now: 'Urgent',
  awareness: 'Update',
  digest: 'Summary',
};

/** Self-service notification inbox — any authenticated user's own notifications
 * (in-app record of truth regardless of which external channels also fired). */
export function NotificationInbox() {
  const { data, loading, error, reload } = useAsync(() => notificationsApi.list(), []);
  const toast = useToast();
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const rows: NotificationRecord[] = Array.isArray(data) ? data : [];
  const unreadCount = rows.filter((n) => !n.readAt).length;
  const sorted = [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const handleMarkRead = async (id: string) => {
    setMarkingId(id);
    try {
      await notificationsApi.markRead(id);
      reload();
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
    } catch (err) {
      toast((err as { message?: string }).message ?? 'Couldn\'t mark the notification as read. Please try again.', 'error');
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationsApi.markAllRead();
      reload();
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));
    } catch (err) {
      toast((err as { message?: string }).message ?? 'Couldn\'t mark notifications as read. Please try again.', 'error');
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="notif-inbox">
      <div className="notif-inbox-header">
        <h2>
          Inbox{unreadCount > 0 && <span className="notif-unread-badge">{unreadCount}</span>}
        </h2>
        {unreadCount > 0 && (
          <Button size="sm" variant="ghost" type="button" className="notif-mark-all" loading={markingAll} onClick={handleMarkAllRead}>
            Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : error ? (
        <p className="text-urgent" role="alert">
          Notifications couldn&apos;t be loaded.{' '}
          <Button size="sm" variant="secondary" type="button" onClick={() => reload()}>Retry</Button>
        </p>
      ) : sorted.length === 0 ? (
        <p className="text-muted">No notifications yet. Updates about your work will appear here.</p>
      ) : (
        <ul className="notif-list">
          {sorted.map((n) => (
            <li key={n.id} className={`notif-item ${n.readAt ? '' : 'notif-unread'}`}>
              <div className="notif-item-main">
                <div className="notif-item-top">
                  {!n.readAt && <span className="notif-dot" aria-hidden />}
                  <span className={`notif-tier notif-tier-${n.tier}`}>{TIER_LABELS[n.tier] ?? humanize(n.tier)}</span>
                  <span className="notif-when">{formatDateTime(n.createdAt)}</span>
                </div>
                <p className="notif-subject">{n.subject}</p>
                <p className="notif-body">{n.body}</p>
                {n.deepLink && (
                  <Link className="notif-link" to={n.deepLink}>
                    Open →
                  </Link>
                )}
              </div>
              {!n.readAt && (
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  className="notif-mark-read"
                  loading={markingId === n.id}
                  onClick={() => handleMarkRead(n.id)}
                >
                  Mark read
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <style>{`
        .notif-inbox {
          background: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .notif-inbox-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-md);
        }
        .notif-inbox-header h2 {
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }
        .notif-unread-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          border-radius: 999px;
          background: var(--color-urgent);
          color: white;
          font-size: 0.6875rem;
          font-weight: 700;
        }
        .notif-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
          max-height: 480px;
          overflow-y: auto;
        }
        .notif-item {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: var(--space-md);
          padding: var(--space-md);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
        }
        .notif-item.notif-unread {
          background: color-mix(in srgb, var(--color-signal) 8%, transparent);
          border-color: color-mix(in srgb, var(--color-signal) 30%, var(--color-border));
        }
        .notif-item-main { flex: 1; min-width: 0; }
        .notif-item-top {
          display: flex;
          gap: var(--space-sm);
          align-items: center;
          margin-bottom: var(--space-xs);
        }
        .notif-tier {
          font-size: 0.6875rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--color-text-muted);
        }
        .notif-when {
          font-size: 0.75rem;
          color: var(--color-text-muted);
        }
        .notif-subject { font-weight: 600; margin-bottom: 2px; }
        .notif-body {
          font-size: 0.875rem;
          color: var(--color-text-muted);
          white-space: pre-wrap;
        }
        .notif-dot { display: none; }
        .notif-link {
          display: inline-block;
          margin-top: var(--space-xs);
          font-size: 0.8125rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
